// ═══════════════════════════════════════════════════════════════════════════════
// Public maintenance status from /api/health (60s poll)
// ═══════════════════════════════════════════════════════════════════════════════

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { MaintenanceMessageLocales, MaintenancePublicStatus } from '@fdh/shared';
import { healthApi } from '../lib/api';
import { getMaintenanceBreakGlassPath } from '../lib/maintenancePaths';

const POLL_MS = 60_000;

type MaintenanceContextValue = {
  isLoading: boolean;
  active: boolean;
  message: MaintenanceMessageLocales;
  adminPortalEnabled: boolean;
  publicApiEnabled: boolean;
  breakGlassPath: string;
  refresh: () => Promise<void>;
};

const emptyMessage = (): MaintenanceMessageLocales => ({
  'zh-Hant': '',
  'zh-Hans': '',
  en: '',
});

const MaintenanceContext = createContext<MaintenanceContextValue | null>(null);

export const useMaintenance = (): MaintenanceContextValue => {
  const ctx = useContext(MaintenanceContext);
  if (!ctx) {
    throw new Error('useMaintenance must be used within MaintenanceProvider');
  }
  return ctx;
};

type HealthMaintenancePayload = {
  maintenance?: MaintenancePublicStatus;
  breakGlassPath?: string;
};

const readPayload = (res: unknown): {
  maintenance: MaintenancePublicStatus | null;
  breakGlassPath: string | null;
} => {
  if (!res || typeof res !== 'object') {
    return { maintenance: null, breakGlassPath: null };
  }
  const payload = res as HealthMaintenancePayload & {
    data?: HealthMaintenancePayload;
  };
  const body = payload.data ?? payload;
  return {
    maintenance: body.maintenance ?? null,
    breakGlassPath: typeof body.breakGlassPath === 'string' ? body.breakGlassPath : null,
  };
};

type MaintenanceProviderProps = {
  children: ReactNode;
};

export const MaintenanceProvider = ({ children }: MaintenanceProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState<MaintenanceMessageLocales>(emptyMessage);
  const [adminPortalEnabled, setAdminPortalEnabled] = useState(false);
  const [publicApiEnabled, setPublicApiEnabled] = useState(true);
  const [breakGlassPath, setBreakGlassPath] = useState(getMaintenanceBreakGlassPath);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const res = await healthApi.get();
      const { maintenance, breakGlassPath: fromHealth } = readPayload(res);
      if (maintenance) {
        setActive(maintenance.active === true);
        setMessage(maintenance.message ?? emptyMessage());
        setAdminPortalEnabled(maintenance.adminPortalEnabled === true);
        setPublicApiEnabled(maintenance.publicApiEnabled !== false);
      } else {
        setActive(false);
      }
      if (fromHealth && fromHealth.startsWith('/')) {
        setBreakGlassPath(fromHealth.replace(/\/+$/, '') || getMaintenanceBreakGlassPath());
      }
    } catch {
      // Keep last known state on transient failures.
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      void refresh();
    }, POLL_MS);
    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    const handleMaintenanceEvent = (): void => {
      void refresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('fdh:maintenance', handleMaintenanceEvent);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('fdh:maintenance', handleMaintenanceEvent);
    };
  }, [refresh]);

  const value = useMemo(
    (): MaintenanceContextValue => ({
      isLoading,
      active,
      message,
      adminPortalEnabled,
      publicApiEnabled,
      breakGlassPath,
      refresh,
    }),
    [
      isLoading,
      active,
      message,
      adminPortalEnabled,
      publicApiEnabled,
      breakGlassPath,
      refresh,
    ],
  );

  return (
    <MaintenanceContext.Provider value={value}>{children}</MaintenanceContext.Provider>
  );
};
